/* ─────────────────────────────────────────────────────────────
   StrictOptionSelect — a `select` drawn from its STATIC options only (ENT-565).

   Used by SelectFieldRenderer when `field.props.multiple` or `field.props.strictOptions` is set.
   - Never falls back to a free-text input: with no options it is a disabled selector that says
     "Not configured".
   - `multiple`: chosen words stay inside the select-like field. The dropdown remains open while
     values are toggled. A saved value the option list no longer holds is appended as a selected
     option, so it remains visible and removable; `props.savedLabels` names it when available.
   - single: clearable only when the field is not required.
   ───────────────────────────────────────────────────────────── */

import type { FieldRendererProps } from '../../config/types';
import { Combobox, type ComboboxOption } from '@/components/ui/Combobox';

export function StrictOptionSelect({ field, value, onChange, error }: FieldRendererProps) {
  const options: ComboboxOption[] =
    field.dataSource?.type === 'static' ? field.dataSource.options.map((o) => ({ value: o.value, label: o.label })) : [];
  const configured = field.dataSource?.type === 'static';
  const disabled = field.isDisabled;
  const savedLabels = (field.props?.savedLabels ?? {}) as Record<string, string>;
  const describedBy = error ? `${field.id}-issues` : undefined;

  if (field.props?.multiple === true) {
    const chosen = Array.isArray(value) ? (value as string[]) : [];
    const configuredValues = new Set(options.map((option) => option.value));
    const multiOptions = [
      ...options,
      ...chosen
        .filter((selected) => !configuredValues.has(selected))
        .map((selected) => ({ value: selected, label: savedLabels[selected] ?? selected })),
    ];
    return (
      <Combobox
        id={field.id}
        mode="multi-inline"
        options={multiOptions}
        value={chosen}
        disabled={disabled || !configured}
        placeholder={configured ? 'Select…' : 'Not configured'}
        ariaLabel={field.label}
        ariaDescribedBy={describedBy}
        onValueChange={(v) => onChange(v)}
      />
    );
  }

  return (
    <Combobox
      id={field.id}
      mode="single"
      options={options}
      value={typeof value === 'string' ? value : ''}
      clearable={!field.resolvedValidations.required}
      disabled={disabled || !configured}
      placeholder={configured ? 'Select…' : 'Not configured'}
      ariaLabel={field.label}
      ariaDescribedBy={describedBy}
      onValueChange={(v) => onChange(v)}
    />
  );
}
